import NoData from "../NoData";
import ReportChart from "./ReportChart";
import { ReportChartConsts } from "../ReportTooltips";
import { VictoryChart, VictoryContainer, VictoryAxis, VictoryLegend, VictoryLine, VictoryBar, VictoryGroup, VictoryScatter } from "victory";
import { abbreviateNumber, colors, monthYearStringToDate } from "../ReportsHelper";
import PropTypes from "prop-types";
import { useContext } from "react";
import { ClientConfigurationContext } from "../../../context/ClientConfigurationContextProvider";

export default function PifWpRetentionChart({ data }) {
    const clientConfiguration = useContext(ClientConfigurationContext);
    const hideWp = clientConfiguration?.hideWpOnPifWpRetChartInReports === true;
    const noData = !data || data?.length === 0;

    if (noData) {
        return (
            <ReportChart {...ReportChartConsts(hideWp ? 'pifRetention' : 'pifWpRetention')} big>
                <NoData />
            </ReportChart>
        );
    }

    const labelColor = document.documentElement.getAttribute('data-gdstheme') === 'dark' ? "#ffffff" : "#000000";

    data = data.map(d => ({
        ...d,
        GroupingKey: monthYearStringToDate(d.GroupingKey),
        Data: {
            ...d.Data,
        }
    }));

    const groupColors = {
        "PIF": colors[0],
        ...(hideWp ? {} : { "Total Written Premium": colors[2] }),
        "184 Day Retention": colors[1],
        "365 Day Retention": colors[3]
    };

    let maxWp = Math.max(...data.map(d => d.Data.WrittenPremium));
    const maxPif = Math.max(...data.map(d => d.Data.Pif));
    if (hideWp) { maxWp = maxPif } //ignore WP
    const pifScaleFactor = maxPif === 0 ? 1 : maxWp / maxPif;
    const maxRetention = 100;
    const retentionScaleFactor = maxWp / maxRetention;

    return (
        <ReportChart {...ReportChartConsts(hideWp ? 'pifRetention' : 'pifWpRetention')} big>
            <VictoryChart
                containerComponent={
                    <VictoryContainer
                        responsive={true}
                    />
                }
            >
                <VictoryLegend
                    x={20}
                    itemsPerRow={2}
                    orientation="horizontal"
                    gutter={20}
                    style={{ labels: { fontSize: 10, fill: labelColor } }}
                    data={Object.entries(groupColors).map(([metric, color]) => ({ name: metric, symbol: { fill: color } }))}
                />
                <VictoryAxis crossAxis
                    style={{
                        tickLabels: { fontSize: 10, fill: labelColor },
                        axis: { stroke: labelColor },
                    }}
                    tickValues={data.map((item) => new Date(item.GroupingKey))
                        .filter((_, index, array) => {
                            const step = Math.ceil(array.length / 10);
                            return index % step === 0;
                        })}
                    tickFormat={(x) => new Date(x).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).replace(' ', '\n')}
                />
                <VictoryAxis dependentAxis
                    domain={{ y: [0, Math.max(hideWp ? 0 : maxWp, maxPif * pifScaleFactor, maxRetention * retentionScaleFactor) * 1.2] }}
                    style={{
                        tickLabels: { fontSize: 10, fill: labelColor },
                        grid: { stroke: labelColor, size: 5 },
                        axis: { stroke: labelColor }
                    }}
                    tickFormat={hideWp ? (x) => `${abbreviateNumber(x / pifScaleFactor)}` : (x) => `$${abbreviateNumber(x)}\n${abbreviateNumber(x / pifScaleFactor)}`}
                />
                <VictoryAxis dependentAxis
                    orientation="right"
                    style={{
                        axis: { stroke: labelColor },
                        tickLabels: { fontSize: 10, fill: labelColor },
                    }}
                    tickFormat={(x) => `${(x / retentionScaleFactor).toFixed(0)}%`}
                />
                <VictoryGroup
                    offset={8}
                    style={{ data: { width: 8 } }}
                >
                    <VictoryBar
                        data={data.map(d => ({
                            x: d.GroupingKey,
                            y: d.Data.Pif * pifScaleFactor
                        }))}
                        style={{ data: { fill: groupColors["PIF"] } }}
                    />
                    {!hideWp && <VictoryBar
                        data={data.map(d => ({
                            x: d.GroupingKey,
                            y: d.Data.WrittenPremium
                        }))}
                        style={{ data: { fill: groupColors["Total Written Premium"] } }}
                    />}
                </VictoryGroup>
                <VictoryLine
                    interpolation="cardinal"
                    data={data.map(d => ({
                        x: d.GroupingKey,
                        y: d.Data.RetentionHalfYear * retentionScaleFactor
                    }))}
                    style={{ data: { stroke: groupColors["184 Day Retention"] } }}
                />
                <VictoryLine
                    interpolation="cardinal"
                    data={data.map(d => ({
                        x: d.GroupingKey,
                        y: d.Data.RetentionFullYear * retentionScaleFactor
                    }))}
                    style={{ data: { stroke: groupColors["365 Day Retention"] } }}
                />
                <VictoryScatter
                    size={2}
                    data={data.map(d => ({
                        x: d.GroupingKey,
                        y: d.Data.RetentionHalfYear * retentionScaleFactor
                    }))}
                    style={{ data: { fill: groupColors["184 Day Retention"] } }}
                />
                <VictoryScatter
                    size={2}
                    data={data.map(d => ({
                        x: d.GroupingKey,
                        y: d.Data.RetentionFullYear * retentionScaleFactor
                    }))}
                    style={{ data: { fill: groupColors["365 Day Retention"] } }}
                />
            </VictoryChart>
        </ReportChart>
    )
}

PifWpRetentionChart.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        GroupingKey: PropTypes.string,
        Data: PropTypes.shape({
            WrittenPremium: PropTypes.number,
            Pif: PropTypes.number,
            RetentionHalfYear: PropTypes.number,
            RetentionFullYear: PropTypes.number
        })
    }))
};